import React, { useCallback, useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import keyMirror from 'keymirror';
import injectSheet from 'react-jss';
import { connect } from 'react-redux';
import { Redirect } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { compose } from 'redux';
import { Col, notification, Row } from 'antd';
import pick from 'lodash/pick';
import get from 'lodash/get';
import noop from 'lodash/noop';
import { BRANCH_LINK_TYPES, createDownloadLink } from '../../lib/branch';
import {
  answerInvitation,
  getCountryCode,
  getInvitationInformation,
  issueToken,
  safeProfileFill,
  signOut,
} from '../../lib/firebase';
import { INVITATION_ERROR_CODES } from '../../lib/invitations';
import { DEFAULT_COUNTRY_CODE } from '../../lib/phone-numbers';
import routes from '../../routes';
import {
  getLinkType,
  getNewContactLinkType,
  getNewContactToken,
  setDownloadLink,
} from '../../store/ducks/branch';
import Spin from '../Common/Spin';
import { classesPropType, historyPropType, profilePropType } from '../prop-types';
import DirectLinkForm from './DirectLink';
import ShareableLinkForm from './ShareableLink';
import { getProfile, getUserId } from '../../store/ducks/profile';
import LoggedInForm from './LoggedInForm';
import CupcakeLogoType from '../CupcakeLogotype';

const GRID_OPTIONS = {
  md: { span: 12, offset: 6 },
  lg: { span: 10, offset: 7 },
  xl: { span: 8, offset: 8 },
};

const NEW_CONTACT_LINK_TYPES = keyMirror({
  shareable: null,
  direct: null,
});

const styles = {
  logotypeContainer: {
    textAlign: 'center',
    marginBottom: '24px',
  },
};

const ERROR_NAMES_BY_CODE = {
  [INVITATION_ERROR_CODES.INVALID_SHAREABLE_TOKEN]: 'invalidShareableToken',
};

function AnswerRequest({
  linkType,
  newContactLinkType,
  newContactToken,
  classes,
  profile,
  currentUserId,
  history: {
    replace,
  },
  saveDownloadLink,
}) {
  const { t } = useTranslation();
  const [invitationInfo, setInvitationInfo] = useState();
  const [countryCode, setCountryCode] = useState();
  const [previousSubmitValue, setPreviousSubmitValue] = useState({});
  const [justSignedIn, setJustSignedIn] = useState(false);
  const hasProfile = Boolean(profile);

  useEffect(
    () => {
      if (!newContactToken) return;
      getInvitationInformation(newContactLinkType, newContactToken)
        .then(({ data }) => setInvitationInfo(data))
        .catch(() => replace(routes.download));
    },
    [newContactLinkType, newContactToken, replace],
  );

  useEffect(
    () => {
      getCountryCode()
        .then(value => setCountryCode(value))
        .catch(() => setCountryCode(DEFAULT_COUNTRY_CODE));
    },
    [],
  );

  const handleSubmit = useCallback(
    (body, rememberMe, uid) => {
      setPreviousSubmitValue({ userData: body, rememberMe });
      return answerInvitation(newContactLinkType, newContactToken, body)
        .then(async () => {
          const replaceTo = uid ? routes.thankYou : routes.answerRequest.contactAdded;
          const downloadLinkPayload = pick(body, 'birthday', 'firstName', 'lastName', 'phoneNumber');
          if (uid) {
            const sessionToken = await issueToken().catch(() => null);
            if (sessionToken) downloadLinkPayload.sessionToken = sessionToken;
            if (!rememberMe) signOut().catch(noop);
          }

          return createDownloadLink(downloadLinkPayload)
            .then((link) => {
              saveDownloadLink(link);
              replace(replaceTo);
            })
            .catch(() => replace(replaceTo));
        })
        .catch((err) => {
          const code = get(err.details, 'code');

          if (ERROR_NAMES_BY_CODE[code]) {
            notification.warning({
              message: t(`apiErrors.${ERROR_NAMES_BY_CODE[code]}.message`),
              description: t(`apiErrors.${ERROR_NAMES_BY_CODE[code]}.description`),
            });
            replace(routes.download);
            return;
          }

          if (code === INVITATION_ERROR_CODES.REQUIRES_AUTHENTICATION) {
            replace(routes.answerRequest.requiresAuthentication);
          } else {
            notification.error({
              message: t('apiErrors.answerFriendshipRequest.message'),
              description: t('apiErrors.answerFriendshipRequest.description'),
            });
            throw err;
          }
        });
    },
    [newContactLinkType, newContactToken, saveDownloadLink, replace, t],
  );

  const handleSubmitWithAuth = useCallback(
    uid => safeProfileFill(uid, previousSubmitValue.userData)
      .then(() => handleSubmit(previousSubmitValue.userData, previousSubmitValue.rememberMe, uid)),
    [handleSubmit, previousSubmitValue],
  );

  const handleSignIn = useCallback(() => {
    setJustSignedIn(true);
  }, []);

  if (!newContactToken || linkType !== BRANCH_LINK_TYPES.newContact) {
    return <Redirect to={routes.download} />;
  }

  if (!invitationInfo || !countryCode) return <Spin cover />;
  if (currentUserId && !hasProfile && !justSignedIn) return <Spin cover />;

  let FormComponent = newContactLinkType === NEW_CONTACT_LINK_TYPES.shareable
    ? ShareableLinkForm
    : DirectLinkForm;

  if (hasProfile && !justSignedIn) {
    FormComponent = LoggedInForm;
  }

  return (
    <Row>
      <Col {...GRID_OPTIONS}>
        <div className={classes.logotypeContainer}>
          <CupcakeLogoType />
        </div>

        <FormComponent
          countryCode={countryCode}
          currentUserId={currentUserId}
          invitationInfo={invitationInfo}
          invitationType={newContactLinkType}
          onSignIn={handleSignIn}
          onSubmit={handleSubmit}
          onSubmitWithAuth={handleSubmitWithAuth}
          phoneNumber={get(previousSubmitValue.userData, 'phoneNumber')}
          profile={profile}
        />
      </Col>
    </Row>
  );
}

AnswerRequest.propTypes = {
  linkType: PropTypes.oneOf(Object.keys(BRANCH_LINK_TYPES)).isRequired,
  newContactLinkType: PropTypes.oneOf(Object.keys(NEW_CONTACT_LINK_TYPES)).isRequired,
  newContactToken: PropTypes.string.isRequired,
  classes: classesPropType.isRequired,
  history: historyPropType.isRequired,
  profile: profilePropType,
  currentUserId: PropTypes.string,
  saveDownloadLink: PropTypes.func.isRequired,
};

AnswerRequest.defaultProps = {
  profile: undefined,
  currentUserId: undefined,
};

function mapStateToProps(state) {
  return {
    linkType: getLinkType(state),
    newContactToken: getNewContactToken(state),
    newContactLinkType: getNewContactLinkType(state),
    profile: getProfile(state),
    currentUserId: getUserId(state),
  };
}

const mapDispatchToProps = {
  saveDownloadLink: setDownloadLink,
};

export default compose(
  connect(mapStateToProps, mapDispatchToProps),
  injectSheet(styles),
)(AnswerRequest);
